import { RedBold } from 'components/Containers';
import { ArchivableLabel } from 'components/patient/blurbs/activities/Layout';
import { useTranslation } from 'context/translations';
import { dateWithoutTimezone } from 'lib/date';
import ArchiveLocationEpisode from 'models/activities/ArchiveLocationEpisode';
import DischargeQuestions from 'models/activities/DischargeQuestions';
import RehabStateChange from 'models/activities/RehabStateChange';
import Activity, { ActivityType } from 'models/Activity';
import { RehabStateApiName } from 'models/RehabState';
import { Label } from 'styles/typography';

type NoteContentHeaderProps = {
  activity: Activity;
};

const NoteContentHeader = (props: NoteContentHeaderProps) => {
  const { activity } = props;
  const { translate, translateAll } = useTranslation();

  if (activity instanceof RehabStateChange) {
    const currentRehabStateApiName = activity.data.currentRehabState.apiName;
    const rehabFacilityText =
      currentRehabStateApiName === RehabStateApiName.Discharged ? '' : `${activity.data.rehabFacility.name} by `;
    let rehabStateText = {
      [RehabStateApiName.Queue]: `added to ${translate('Queue')} at`,
      admission: 'admitted to', // maintain for backwards compatibility
      [RehabStateApiName.InTreatment]: `moved to ${translate(activity.data.currentRehabState.state)} at`,
      [RehabStateApiName.Discharged]: '<>',
    }[currentRehabStateApiName];
    if (currentRehabStateApiName === RehabStateApiName.Discharged) {
      const reason = activity.data.dischargeInfo.reason || '';

      const dischargeReason = reason.charAt(0).toLowerCase() + reason.slice(1);
      const dischargeGroup = activity.data.dischargeInfo.dischargedTo;
      const againstMedicalAdvice = activity.data.dischargeInfo.againstMedicalAdvice;

      const dischargeReasonText =
        (dischargeReason || `${translate('discharged').toLowerCase()}; no reason available`) +
        (againstMedicalAdvice ? ' (against medical advice)' : '');
      const dischargeGroupText = dischargeGroup ? `to ${dischargeGroup} ` : '';

      rehabStateText = `was ${dischargeReasonText}. ${translate('Discharged')} from ${activity.data.rehabFacility.name} ${dischargeGroupText}by`;
    }
    return (
      <ArchivableLabel $archived={activity.archived}>
        {activity.patient?.name} {rehabStateText} {rehabFacilityText}
        <b> {activity.createdBy.fullName} </b>
        on {dateWithoutTimezone(activity.enteredAt!)}
      </ArchivableLabel>
    );
  }
  if (activity instanceof ArchiveLocationEpisode) {
    return (
      <Label>
        {activity.patient?.name} record at {activity.data.rehabFacility.name} was canceled by{' '}
        <b>{activity.createdBy.fullName}</b> on {dateWithoutTimezone(activity.createdAt!)}
      </Label>
    );
  }
  return (
    <Label>
      {activity instanceof DischargeQuestions && !activity.archived && activity.edited && <i>(Edited) </i>}
      {activity.escalation?.isDefault ? (
        <RedBold>Escalation {translateAll(activity.typeLabel)}</RedBold>
      ) : (
        translateAll(activity.typeLabel)
      )}
      {!!activity.attachments?.length && ' (with attachments)'}
      {[
        ActivityType.IMPORT_PATIENT_FIELD_CHANGES,
        ActivityType.PATIENT_FIELD_CHANGES,
        ActivityType.REFERRAL_DEMOGRAPHICS_UPDATES,
      ].includes(activity.type)
        ? ' updated by '
        : ' added by '}
      <b>{activity.createdBy.fullName}</b>
      {activity.createdByGroup?.name && ` at ${activity.createdByGroup.name}`}
    </Label>
  );
};

export default NoteContentHeader;
